import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  ArrowLeft,
} from "lucide-react";
import { ClickableText } from "@/components/dictionary/ClickableWord";
import { DictionaryPopup } from "@/components/dictionary/DictionaryPopup";
import { useDictionaryStore } from "@/stores/dictionaryStore";

// Episode data imports
import episode001 from "@/data/episode-001.json";
import episode002 from "@/data/episode-002.json";

// Episode data mapping
const episodes: Record<string, typeof episode001> = {
  "1": episode001,
  "2": episode002,
};

const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2] as const;

export default function EpisodePage() {
  const params = useParams();
  const episodeId = params.id || "1";
  const episode = episodes[episodeId] || episode001;

  // Audio ref
  const audioRef = useRef<HTMLAudioElement>(null);
  const transcriptContainerRef = useRef<HTMLDivElement>(null);
  const segmentRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Player state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(episode.duration || 0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [volume, _setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Sync state
  const [activeSegmentIndex, setActiveSegmentIndex] = useState(-1);
  const [activeWordIndex, setActiveWordIndex] = useState(-1);
  const [autoScroll, setAutoScroll] = useState(true);

  // Dictionary store
  const { isOpen, entry, openPopup, closePopup } = useDictionaryStore();

  // Get segments
  const segments = episode.transcript?.segments || [];

  // ===== Audio Event Handlers =====
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsLoading(false);
    };

    const onCanPlay = () => {
      setIsLoading(false);
    };

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    const onWaiting = () => setIsLoading(true);
    const onPlaying = () => setIsLoading(false);

    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("canplay", onCanPlay);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("waiting", onWaiting);
    audio.addEventListener("playing", onPlaying);

    return () => {
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("canplay", onCanPlay);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("waiting", onWaiting);
      audio.removeEventListener("playing", onPlaying);
    };
  }, []);

  // ===== High-precision time sync with requestAnimationFrame =====
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    let animationId: number;

    const updateTime = () => {
      if (audio && !audio.paused) {
        setCurrentTime(audio.currentTime);
        animationId = requestAnimationFrame(updateTime);
      }
    };

    if (isPlaying) {
      animationId = requestAnimationFrame(updateTime);
    }

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [isPlaying]);

  // ===== Find Active Segment & Word =====
  useEffect(() => {
    if (segments.length === 0) return;

    // Find active segment - linear search is more reliable for gaps between segments
    let segmentIndex = -1;
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      // Use small buffer (0.05s) to handle timing gaps
      if (currentTime >= seg.start - 0.05 && currentTime < seg.end + 0.05) {
        segmentIndex = i;
        break;
      }
    }

    // If no exact match, find the closest upcoming segment
    if (segmentIndex === -1) {
      for (let i = 0; i < segments.length; i++) {
        if (segments[i].start > currentTime) {
          // We're in a gap before this segment
          if (i > 0 && currentTime > segments[i - 1].end) {
            segmentIndex = i - 1; // Stay on previous segment
          }
          break;
        }
      }
    }

    // Update segment index
    if (segmentIndex !== activeSegmentIndex) {
      setActiveSegmentIndex(segmentIndex);
    }

    // Find active word within segment
    if (segmentIndex !== -1) {
      const segment = segments[segmentIndex];
      if (segment.words && segment.words.length > 0) {
        let wordIndex = -1;
        for (let i = 0; i < segment.words.length; i++) {
          const word = segment.words[i];
          // Use small buffer for word timing too
          if (
            currentTime >= word.start - 0.02 &&
            currentTime < word.end + 0.02
          ) {
            wordIndex = i;
            break;
          }
        }
        // If between words, find closest
        if (wordIndex === -1) {
          for (let i = 0; i < segment.words.length; i++) {
            const word = segment.words[i];
            if (currentTime < word.start) {
              wordIndex = Math.max(0, i - 1);
              break;
            }
            if (i === segment.words.length - 1 && currentTime >= word.start) {
              wordIndex = i;
            }
          }
        }
        if (wordIndex !== activeWordIndex) {
          setActiveWordIndex(wordIndex);
        }
      } else if (activeWordIndex !== -1) {
        setActiveWordIndex(-1);
      }
    } else if (activeWordIndex !== -1) {
      setActiveWordIndex(-1);
    }
  }, [currentTime, segments, activeSegmentIndex, activeWordIndex]);

  // ===== Auto-scroll to Active Segment =====
  useEffect(() => {
    if (!autoScroll || activeSegmentIndex < 0) return;

    const segment = segments[activeSegmentIndex];
    if (!segment) return;

    const segmentEl = segmentRefs.current.get(segment.id);
    if (segmentEl && transcriptContainerRef.current) {
      const container = transcriptContainerRef.current;
      const segmentTop = segmentEl.offsetTop;
      const containerHeight = container.clientHeight;
      const scrollTarget = segmentTop - containerHeight / 3;

      container.scrollTo({
        top: scrollTarget,
        behavior: "smooth",
      });
    }
  }, [activeSegmentIndex, autoScroll, segments]);

  // ===== Player Controls =====
  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(console.error);
    }
  }, [isPlaying]);

  const seek = useCallback(
    (time: number) => {
      const audio = audioRef.current;
      if (!audio) return;

      const clampedTime = Math.max(
        0,
        Math.min(time, audio.duration || duration)
      );
      audio.currentTime = clampedTime;
      setCurrentTime(clampedTime);
    },
    [duration]
  );

  const skipForward = useCallback(() => {
    seek(currentTime + 30);
  }, [currentTime, seek]);

  const skipBackward = useCallback(() => {
    seek(currentTime - 15);
  }, [currentTime, seek]);

  const cyclePlaybackRate = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const currentIndex = PLAYBACK_RATES.indexOf(
      playbackRate as (typeof PLAYBACK_RATES)[number]
    );
    const nextIndex = (currentIndex + 1) % PLAYBACK_RATES.length;
    const newRate = PLAYBACK_RATES[nextIndex];

    audio.playbackRate = newRate;
    setPlaybackRate(newRate);
  }, [playbackRate]);

  const toggleMute = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isMuted) {
      audio.volume = volume;
      setIsMuted(false);
    } else {
      audio.volume = 0;
      setIsMuted(true);
    }
  }, [isMuted, volume]);

  // Handle segment click
  const handleSegmentClick = useCallback(
    (startTime: number) => {
      seek(startTime);
    },
    [seek]
  );

  // Handle progress bar click
  const handleProgressClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const percentage = clickX / rect.width;
      const newTime = percentage * duration;
      seek(newTime);
    },
    [duration, seek]
  );

  // Format time
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Word click handler
  const handleWordClick = (word: string) => {
    openPopup(word);
  };

  // Render word with highlight
  const renderWordWithHighlight = (text: string, segmentIndex: number) => {
    const segment = segments[segmentIndex];
    if (!segment?.words || activeSegmentIndex !== segmentIndex) {
      return <ClickableText text={text} onWordClick={handleWordClick} />;
    }

    // Split text into words and render with highlights
    const words = segment.words;

    return (
      <span>
        {words.map((wordData, wIdx) => {
          const isActiveWord = activeWordIndex === wIdx;
          const wordText = wordData.word;

          return (
            <span
              key={wIdx}
              className={`
                transition-all duration-150 cursor-pointer
                ${
                  isActiveWord
                    ? "bg-accent-blue/40 text-white rounded px-0.5 -mx-0.5"
                    : "hover:text-accent-blue"
                }
              `}
              onClick={(e) => {
                e.stopPropagation();
                handleWordClick(wordText.replace(/[.,!?;:]/g, ""));
              }}
            >
              {wordText}
              {wIdx < words.length - 1 ? " " : ""}
            </span>
          );
        })}
      </span>
    );
  };

  return (
    <main className="min-h-screen p-4 md:p-8 lg:p-12 font-body">
      {/* Hidden Audio Element */}
      <audio
        ref={audioRef}
        src={episode.audio_url}
        preload="metadata"
        className="hidden"
      />

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          {/* Back Button */}
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-txt-secondary hover:text-white transition-colors mb-4 group"
          >
            <span className="p-2 rounded-full bg-white/5 group-hover:bg-white/10 transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </span>
            <span className="text-sm">Ana Sayfa</span>
          </Link>

          <h1 className="font-display text-3xl md:text-4xl text-gradient">
            {episode.title}
          </h1>
          <p className="text-txt-secondary mt-2">{episode.title_tr}</p>
          <p className="text-txt-muted text-sm mt-1">
            {episode.description_tr}
          </p>
        </motion.header>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Panel - Audio Player */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card p-6"
          >
            {/* Cover Image */}
            <div
              className={`relative aspect-square max-w-md mx-auto mb-6 rounded-2xl overflow-hidden bg-gradient-to-br ${
                episodeId === "2"
                  ? "from-emerald-500 to-teal-700"
                  : "from-blue-500 to-orange-500"
              }`}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <span className="absolute top-4 right-4 chapter-badge">
                    <span>
                      {episodeId === "2" ? "Yrittäjä Opas" : "Suomen Uutiset"}
                    </span>
                  </span>
                  <h2 className="font-display text-4xl md:text-5xl text-white italic px-4">
                    {episodeId === "2" ? "Yrittäjyys" : "Suomi"}
                  </h2>
                  <p className="text-white text-lg tracking-widest uppercase mt-2">
                    {episodeId === "2" ? "Rahoitus & YEL" : "Tammikuu 2026"}
                  </p>
                </div>
              </div>
            </div>

            {/* Episode Info */}
            <div className="text-center mb-6">
              <p className="text-white/60 text-sm">
                {new Date(episode.published_at).toLocaleDateString("tr-TR", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </p>
              <h3 className="font-display text-xl text-white mt-1">
                {episode.title}
              </h3>
              <p className="text-white/70 text-sm mt-1">
                {episode.description}
              </p>
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
              <div
                className="progress-bar cursor-pointer"
                onClick={handleProgressClick}
              >
                <div
                  className="progress-bar-fill"
                  style={{
                    width: `${(currentTime / duration) * 100}%`,
                  }}
                />
              </div>
              <div className="flex justify-between text-sm text-txt-muted mt-2">
                <span>{formatTime(currentTime)}</span>
                <span>-{formatTime(duration - currentTime)}</span>
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center gap-6">
              {/* Speed */}
              <button
                className="text-txt-secondary hover:text-white transition-colors min-w-[40px]"
                onClick={cyclePlaybackRate}
                title="Oynatma hızı"
              >
                {playbackRate}×
              </button>

              {/* Skip Back */}
              <button
                className="player-btn"
                onClick={skipBackward}
                title="15 saniye geri"
              >
                <SkipBack className="w-5 h-5" />
                <span className="sr-only">15 saniye geri</span>
              </button>

              {/* Play/Pause */}
              <button
                className="player-btn-primary"
                onClick={togglePlay}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : isPlaying ? (
                  <Pause className="w-8 h-8" />
                ) : (
                  <Play className="w-8 h-8 ml-1" />
                )}
              </button>

              {/* Skip Forward */}
              <button
                className="player-btn"
                onClick={skipForward}
                title="30 saniye ileri"
              >
                <SkipForward className="w-5 h-5" />
                <span className="sr-only">30 saniye ileri</span>
              </button>

              {/* Volume */}
              <button
                className="text-txt-secondary hover:text-white transition-colors"
                onClick={toggleMute}
                title={isMuted ? "Sesi aç" : "Sesi kapat"}
              >
                {isMuted ? (
                  <VolumeX className="w-5 h-5" />
                ) : (
                  <Volume2 className="w-5 h-5" />
                )}
              </button>
            </div>

            {/* Auto-scroll toggle */}
            <div className="mt-4 flex items-center justify-center gap-3">
              <button
                onClick={() => setAutoScroll(!autoScroll)}
                className={`
                  relative w-12 h-7 rounded-full transition-all duration-300
                  ${
                    autoScroll
                      ? "bg-accent-purple shadow-[0_0_12px_rgba(139,92,246,0.4)]"
                      : "bg-white/10"
                  }
                `}
                aria-pressed={autoScroll}
                aria-label="Otomatik kaydırma"
              >
                <span
                  className={`
                    absolute top-1 left-1 w-5 h-5 rounded-full bg-white
                    transition-all duration-300 ease-out
                    ${autoScroll ? "translate-x-5" : "translate-x-0"}
                  `}
                />
              </button>
              <span
                className={`text-sm transition-colors duration-200 ${
                  autoScroll ? "text-white" : "text-txt-muted"
                }`}
              >
                Otomatik kaydırma
              </span>
            </div>

            {/* Episode Source & Audio Origin */}
            {episode.source && (
              <div className="mt-6 pt-4 border-t border-white/10 text-xs text-txt-secondary leading-relaxed">
                <p>
                  <span className="font-semibold text-white/90">
                    Lähde / Kaynak:{" "}
                  </span>
                  <span className="text-white/80">{episode.source.title}</span>
                  {episode.source.publisher && (
                    <span> ({episode.source.publisher})</span>
                  )}
                  {episode.source.url && /^https?:\/\//.test(episode.source.url) && (
                    <>
                      {" — "}
                      <a
                        href={episode.source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-accent-blue hover:underline"
                      >
                        {episode.source.url}
                      </a>
                    </>
                  )}
                  {episode.source.note && (
                    <span className="text-txt-muted">
                      {" • "}
                      <span className="font-semibold text-white/75">Ääni / Ses:</span>{" "}
                      {episode.source.note}
                    </span>
                  )}
                  {" • "}
                  <a
                    href="https://project-info.mydesignbox.fi/gizlilik/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-txt-muted hover:text-white underline underline-offset-2"
                  >
                    Tietosuoja / Gizlilik
                  </a>
                </p>
              </div>
            )}
          </motion.div>

          {/* Right Panel - Transcript */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-card p-6"
          >
            {/* Transcript Content */}
            <div
              ref={transcriptContainerRef}
              className="space-y-1 transcript-fade max-h-[500px] overflow-y-auto pr-2"
            >
              {segments.map((segment: any, index: number) => (
                <motion.div
                  key={segment.id}
                  ref={(el) => {
                    if (el) segmentRefs.current.set(segment.id, el);
                  }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.02 }}
                  className={`transcript-line cursor-pointer ${
                    index === activeSegmentIndex ? "transcript-active" : ""
                  }`}
                  onClick={() => handleSegmentClick(segment.start)}
                >
                  {/* Timestamp badge */}
                  <span className="text-xs text-txt-muted opacity-60 mr-2">
                    {formatTime(segment.start)}
                  </span>

                  {/* Finnish - With Word Highlighting */}
                  <p
                    className={`font-transcript text-lg leading-relaxed ${
                      index === activeSegmentIndex
                        ? "text-white"
                        : index < activeSegmentIndex
                        ? "text-txt-muted"
                        : "text-txt-secondary"
                    }`}
                  >
                    {renderWordWithHighlight(segment.text, index)}
                  </p>

                  {/* Turkish */}
                  <p
                    className={`font-body text-sm mt-1 ${
                      index === activeSegmentIndex
                        ? "text-accent-blue"
                        : "text-txt-muted"
                    }`}
                  >
                    {segment.text_tr}
                  </p>
                </motion.div>
              ))}
            </div>

            {/* Language Legend */}
            <div className="flex gap-6 mt-6 pt-4 border-t border-white/10">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-white/70" />
                <span className="text-sm text-txt-secondary">
                  Suomi (Finnish)
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-accent-blue" />
                <span className="text-sm text-txt-secondary">Türkçe</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Footer */}
        <footer className="mt-12 pt-6 border-t border-white/10 text-center text-xs text-txt-muted">
          <a
            href="https://project-info.mydesignbox.fi/gizlilik/"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-white transition-colors underline underline-offset-2"
          >
            Tietosuoja / Gizlilik
          </a>
        </footer>
      </div>

      {/* Dictionary Popup */}
      <DictionaryPopup entry={entry} isOpen={isOpen} onClose={closePopup} />
    </main>
  );
}
